import { format, getISOWeek } from "date-fns";
import { Fragment, type ReactNode, useMemo } from "react";
import type { DateRange } from "@/shared/hooks/useDateParams";
import { cn } from "@/v2/lib/utils";
import { buildCalendarGrid, getWeekdayLabels } from "@/v2/lib/weekUtils";

function isSameDay(a: Date, b: Date) {
	return (
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate()
	);
}

function isSameMonth(a: Date, b: Date) {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function isInRange(d: Date, from: Date | undefined, to: Date | undefined) {
	if (!from || !to) return false;
	return d.getTime() >= from.getTime() && d.getTime() <= to.getTime();
}

/** Everything a day-slot needs to render its cell — the date plus the flags the
 * grid already computed for highlighting. */
export interface CalendarDayContext {
	date: Date;
	isToday: boolean;
	/** False for the leading/trailing spill days from adjacent months. */
	inMonth: boolean;
	/** True at the days where the month flips (the spill-day boundaries). */
	showMonthPrefix: boolean;
	inRange: boolean;
	isRangeStart: boolean;
	isRangeEnd: boolean;
	isSelected: boolean;
}

/** Everything a week-slot needs — the row's days plus its ISO week number. */
export interface CalendarWeekContext {
	/** First day of the row (per the firm's week-start setting). */
	weekStart: Date;
	/** Last day of the row. */
	weekEnd: Date;
	/** All seven days of the row. */
	days: Date[];
	/** ISO week number. */
	weekNumber: number;
}

interface Calendar2GridProps {
	/** Any date within the month to display. */
	month: Date;
	/** The active single date — ring-highlighted when no [from, to] range is set. */
	selectedDate?: Date;
	/** The active [from, to] range — its days are highlighted when both are set. */
	dateRange?: DateRange;
	/** Show the week-number column on the right. Defaults to true. */
	showWeekColumn?: boolean;
	/**
	 * Slot for a day cell's interior. Receives the day and its computed flags.
	 * Falls back to the bare day heading when omitted. The grid owns the cell
	 * chrome (borders, highlight, today ring, spill-day dimming, click); the
	 * slot owns the content.
	 */
	renderDay?: (ctx: CalendarDayContext) => ReactNode;
	/** Slot for a week cell's interior. Falls back to the bare "W##" label. */
	renderWeek?: (ctx: CalendarWeekContext) => ReactNode;
	onDayClick?: (day: Date) => void;
	onWeekClick?: (weekStart: Date, weekEnd: Date) => void;
}

/**
 * Canonical day heading — the optional month prefix plus the day number (with
 * the today circle). Exported so slot implementations get the standard look for
 * free instead of re-deriving it.
 */
export function CalendarDayHeading({
	ctx,
	className,
}: {
	ctx: CalendarDayContext;
	className?: string;
}) {
	return (
		<div className={cn("flex items-center gap-1 min-w-0", className)}>
			{ctx.showMonthPrefix && (
				<span className="text-[10px] leading-none text-muted-foreground sm:text-xs">
					{format(ctx.date, "MMM")}
				</span>
			)}
			{ctx.isToday ? (
				<span className="flex h-6 w-6 items-center justify-center rounded-full bg-highlight text-sm text-white">
					{ctx.date.getDate()}
				</span>
			) : (
				<span className="text-sm leading-none text-muted-foreground">
					{ctx.date.getDate()}
				</span>
			)}
		</div>
	);
}

/**
 * Generic month grid: weekday headers, a day cell per date, and an optional
 * week-number column. It is intentionally content-agnostic — it lays out the
 * grid, computes per-day highlight state, and handles clicks, then defers the
 * cell *contents* to the renderDay / renderWeek slots (each gets the date).
 * Navigation/header lives in the parent (e.g. Calendar2View).
 */
export function Calendar2Grid({
	month,
	selectedDate,
	dateRange,
	showWeekColumn = true,
	renderDay,
	renderWeek,
	onDayClick,
	onWeekClick,
}: Calendar2GridProps) {
	const hasRange = !!(dateRange?.from && dateRange?.to);
	const today = useMemo(() => new Date(), []);
	const year = month.getFullYear();
	const monthIndex = month.getMonth();
	const monthStart = useMemo(
		() => new Date(year, monthIndex, 1),
		[year, monthIndex],
	);

	const { days, weeksNeeded } = useMemo(
		() => buildCalendarGrid(year, monthIndex),
		[year, monthIndex],
	);

	const weekdayFull = getWeekdayLabels("full");
	const weekdayLetter = getWeekdayLabels("letter");

	return (
		<div
			className="grid flex-1 min-h-0"
			style={{
				gridTemplateColumns: showWeekColumn
					? "repeat(7, 1fr) auto"
					: "repeat(7, 1fr)",
				gridTemplateRows: `auto repeat(${weeksNeeded}, 1fr)`,
			}}
		>
			{/* Weekday headers */}
			{weekdayFull.map((label, i) => (
				<div
					key={label + String(i)}
					className={cn(
						"border-b border-border py-2 text-xs text-muted-foreground sm:py-2.5 sm:text-[13px]",
						"bg-neutral-100 dark:bg-neutral-800",
						"text-center sm:pl-3 sm:text-left",
						i > 0 && "border-l border-l-border",
					)}
				>
					<span className="sm:hidden">{weekdayLetter[i]}</span>
					<span className="hidden sm:inline">{label}</span>
				</div>
			))}
			{/* Week column header */}
			{showWeekColumn && (
				<div className="border-b border-l-2 border-border border-l-border bg-neutral-100 py-2 text-center text-xs text-muted-foreground dark:bg-neutral-800 sm:py-2.5 sm:px-3 sm:text-[13px]">
					<span className="sm:hidden">Wk</span>
					<span className="hidden sm:inline">Week</span>
				</div>
			)}

			{/* Rows */}
			{Array.from({ length: weeksNeeded }).map((_, weekIdx) => {
				const weekDays = days.slice(weekIdx * 7, weekIdx * 7 + 7);
				const weekNumber = getISOWeek(weekDays[3]);

				return (
					<Fragment key={weekIdx}>
						{weekDays.map((day, col) => {
							const inMonth = isSameMonth(day, monthStart);
							const isToday = isSameDay(day, today);
							const inRange =
								hasRange && isInRange(day, dateRange?.from, dateRange?.to);
							const isRangeStart = !!(
								hasRange &&
								dateRange?.from &&
								isSameDay(day, dateRange.from)
							);
							const isRangeEnd = !!(
								hasRange &&
								dateRange?.to &&
								isSameDay(day, dateRange.to)
							);
							const isSelected = !!(
								!hasRange &&
								selectedDate &&
								isSameDay(day, selectedDate)
							);
							// Flag the days where the month flips (the leading/trailing
							// spill days) so a slot can prefix the month name.
							const globalIdx = weekIdx * 7 + col;
							const prevDay = globalIdx > 0 ? days[globalIdx - 1] : null;
							const nextDay =
								globalIdx < days.length - 1 ? days[globalIdx + 1] : null;
							const showMonthPrefix = !!(
								(prevDay && prevDay.getMonth() !== day.getMonth()) ||
								(nextDay && nextDay.getMonth() !== day.getMonth())
							);

							const ctx: CalendarDayContext = {
								date: day,
								isToday,
								inMonth,
								showMonthPrefix,
								inRange,
								isRangeStart,
								isRangeEnd,
								isSelected,
							};

							return (
								<button
									type="button"
									key={`${weekIdx}-${col}`}
									onClick={() => onDayClick?.(day)}
									className={cn(
										"flex flex-col border-t border-border bg-background p-1 text-left transition-colors hover:bg-muted/50 sm:p-2",
										col > 0 && "border-l border-l-border",
										inRange && "bg-primary/5",
										isRangeStart && "bg-primary/8",
										isRangeEnd && "bg-primary/8",
										isToday && "ring-[1.5px] ring-inset ring-highlight",
										isSelected &&
											!isToday &&
											"ring-[1.5px] ring-inset ring-primary",
									)}
								>
									<div
										className={cn(
											"flex flex-1 flex-col w-full min-h-0",
											!inMonth && "opacity-40",
										)}
									>
										{renderDay ? (
											renderDay(ctx)
										) : (
											<CalendarDayHeading ctx={ctx} />
										)}
									</div>
								</button>
							);
						})}

						{/* Week cell */}
						{showWeekColumn && (
							<button
								type="button"
								onClick={() => onWeekClick?.(weekDays[0], weekDays[6])}
								className="flex flex-col items-center border-t border-l-2 border-border border-l-border bg-muted/20 p-1 transition-colors hover:bg-muted/40 sm:p-2"
							>
								{renderWeek ? (
									renderWeek({
										weekStart: weekDays[0],
										weekEnd: weekDays[6],
										days: weekDays,
										weekNumber,
									})
								) : (
									<span className="text-[10px] font-medium text-muted-foreground/60">
										W{weekNumber}
									</span>
								)}
							</button>
						)}
					</Fragment>
				);
			})}
		</div>
	);
}
